import React, { useState } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { Input } from '../../design-system/Input';
import { Badge } from '../../design-system/Badge';
import {
  Search,
  CheckSquare,
  Layers,
  User,
  ArrowRight
} from 'lucide-react';

export const GlobalSearchPage = ({ setCurrentView, setCurrentProjectView }) => {
  const {
    projects = [],
    allTasks = [],
    members = [],
    setActiveProjectId,
    setSelectedTask
  } = useWorkspace();

  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all'); // 'all', 'tasks', 'projects', 'people'

  const q = query.trim().toLowerCase();

  const matchingTasks = allTasks.filter(
    (t) =>
      !q ||
      t.title.toLowerCase().includes(q) ||
      t.id.toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q) ||
      (t.labels || []).some((l) => l.toLowerCase().includes(q))
  );

  const matchingProjects = projects.filter(
    (p) =>
      !q ||
      p.name.toLowerCase().includes(q) ||
      p.key.toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q) ||
      p.category.toLowerCase().includes(q)
  );

  const matchingMembers = members.filter(
    (m) =>
      !q ||
      m.user.name.toLowerCase().includes(q) ||
      m.user.email.toLowerCase().includes(q) ||
      m.role.toLowerCase().includes(q)
  );

  const totalResults = matchingTasks.length + matchingProjects.length + matchingMembers.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} className="animate-fade-in">
      {/* Search Header */}
      <div>
        <h1 style={{ fontSize: '24px', fontWeight: '800' }}>Global Search</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
          Find tasks, projects, people, and workspace knowledge instantly
        </p>
      </div>

      {/* Search Bar */}
      <div style={{ maxWidth: '640px' }}>
        <Input
          placeholder="Search by keywords, task keys (e.g. FOR-101), project names, or team members..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          icon={<Search size={18} />}
          style={{ fontSize: '15px' }}
          autoFocus
        />
      </div>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border-divider)', gap: '8px' }}>
        {[
          { id: 'all', label: 'All Results', count: totalResults },
          { id: 'tasks', label: 'Tasks', count: matchingTasks.length },
          { id: 'projects', label: 'Projects', count: matchingProjects.length },
          { id: 'people', label: 'People & Teams', count: matchingMembers.length }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveCategory(tab.id)}
            style={{
              padding: '10px 16px',
              border: 'none',
              background: 'none',
              borderBottom: activeCategory === tab.id ? '2px solid var(--accent-teal)' : '2px solid transparent',
              color: activeCategory === tab.id ? 'var(--accent-teal)' : 'var(--text-secondary)',
              fontSize: '13px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <span>{tab.label}</span>
            <Badge size="xs" variant={activeCategory === tab.id ? 'teal' : 'neutral'}>
              {tab.count}
            </Badge>
          </button>
        ))}
      </div>

      {/* Results Container */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* PROJECTS SECTION */}
        {(activeCategory === 'all' || activeCategory === 'projects') && matchingProjects.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Layers size={16} color="var(--accent-teal)" />
              <h2 style={{ fontSize: '15px', fontWeight: '800', color: 'var(--text-primary)' }}>
                Projects ({matchingProjects.length})
              </h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px' }}>
              {matchingProjects.map((p) => (
                <div
                  key={p.id}
                  onClick={() => {
                    setActiveProjectId(p.id);
                    setCurrentView('project_view');
                    if (setCurrentProjectView) setCurrentProjectView('board');
                  }}
                  style={{
                    padding: '16px',
                    backgroundColor: 'var(--surface-card)',
                    boxShadow: 'var(--shadow-raised-sm)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--border-subtle)',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    transition: 'all 0.15s'
                  }}
                  className="interactive-raised"
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '11px', fontWeight: '800', color: 'var(--accent-teal)' }}>
                      {p.key}
                    </span>
                    <Badge variant="recessed" size="xs">{p.category}</Badge>
                  </div>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>
                    {p.name}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                    {p.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TASKS SECTION */}
        {(activeCategory === 'all' || activeCategory === 'tasks') && matchingTasks.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <CheckSquare size={16} color="var(--color-info)" />
              <h2 style={{ fontSize: '15px', fontWeight: '800', color: 'var(--text-primary)' }}>
                Tasks ({matchingTasks.length})
              </h2>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {matchingTasks.map((t) => (
                <div
                  key={t.id}
                  onClick={() => setSelectedTask(t)}
                  style={{
                    padding: '14px 18px',
                    backgroundColor: 'var(--surface-card)',
                    boxShadow: 'var(--shadow-raised-sm)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-subtle)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                  className="interactive-raised"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <span style={{ fontSize: '12px', fontWeight: '800', color: 'var(--text-muted)' }}>
                      {t.id}
                    </span>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)' }}>
                        {t.title}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        {t.description?.substring(0, 90)}...
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Badge variant={t.status === 'done' ? 'success' : 'teal'} size="xs">
                      {t.status.replace('_', ' ')}
                    </Badge>
                    <Badge
                      variant={
                        t.priority === 'urgent'
                          ? 'danger'
                          : t.priority === 'high'
                          ? 'warning'
                          : 'neutral'
                      }
                      size="xs"
                    >
                      {t.priority}
                    </Badge>
                    <ArrowRight size={15} color="var(--text-muted)" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MEMBERS SECTION */}
        {(activeCategory === 'all' || activeCategory === 'people') && matchingMembers.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <User size={16} color="var(--color-purple)" />
              <h2 style={{ fontSize: '15px', fontWeight: '800', color: 'var(--text-primary)' }}>
                People & Collaborators ({matchingMembers.length})
              </h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
              {matchingMembers.map((m) => (
                <div
                  key={m.membershipId}
                  onClick={() => setCurrentView('members')}
                  style={{
                    padding: '14px 16px',
                    backgroundColor: 'var(--surface-card)',
                    boxShadow: 'var(--shadow-raised-sm)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--border-subtle)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer'
                  }}
                  className="interactive-raised"
                >
                  <img
                    src={m.user.avatar}
                    alt={m.user.name}
                    style={{ width: '36px', height: '36px', borderRadius: '999px', objectFit: 'cover' }}
                  />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                      {m.user.name}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                      {m.role} • {m.user.email}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {totalResults === 0 && (
          <div
            style={{
              padding: '60px 20px',
              textAlign: 'center',
              backgroundColor: 'var(--surface-recessed)',
              borderRadius: 'var(--radius-card)',
              color: 'var(--text-muted)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Search size={32} color="var(--accent-teal)" style={{ opacity: 0.8 }} />
            <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>
              No matches found for "{query}"
            </div>
            <div style={{ fontSize: '13px' }}>Try searching by project key, task title, or team member name.</div>
          </div>
        )}
      </div>
    </div>
  );
};
